import React from "react";
import { motion, MotionProps } from "framer-motion";

// --- Composant Primitive ---

type AsProp =
  | keyof JSX.IntrinsicElements
  | React.ForwardRefExoticComponent<any>;

type PolymorphicRef<As extends AsProp> = React.ComponentPropsWithRef<As>["ref"];

type PrimitiveProps<As extends AsProp> = {
  as?: As;
  children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<As>, "as" | "children"> &
  MotionProps;

const Primitive = React.forwardRef(
  <As extends AsProp = "div">(
    props: PrimitiveProps<As>,
    ref: PolymorphicRef<As>,
  ) => {
    const { as, children, ...rest } = props;
    const Component = (as || "div") as
      | keyof JSX.IntrinsicElements
      | React.ForwardRefExoticComponent<any>;

    const MotionComponent = motion(Component);

    return (
      <MotionComponent ref={ref} {...rest}>
        {children}
      </MotionComponent>
    );
  },
) as <As extends AsProp = "div">(
  props: PrimitiveProps<As> & { ref?: PolymorphicRef<As> },
) => React.ReactElement | null;

export { Primitive, type PrimitiveProps };
